import { useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Search, Home, ChevronRight, MapPin, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  buildPropertyPlans,
  applyPlans,
  type PropertyPlan,
} from "@/features/properties/services/deliveryStateRecompute";
import { RecomputeReviewDialog } from "./RecomputeReviewDialog";

interface PropertyItem {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  property_type?: string;
}

interface PropertySelectorProps {
  properties: PropertyItem[];
  projectName: string;
  onSelect: (property: PropertyItem) => void;
  onApplied?: () => void;
}

export function PropertySelector({
  properties,
  projectName,
  onSelect,
  onApplied,
}: PropertySelectorProps) {
  const [search, setSearch] = useState("");
  const [recomputePlans, setRecomputePlans] = useState<PropertyPlan[] | null>(null);
  const [recomputeOpen, setRecomputeOpen] = useState(false);
  const [buildingPlan, setBuildingPlan] = useState(false);
  const [applyingPlan, setApplyingPlan] = useState(false);

  const handleRecomputeAll = async () => {
    if (buildingPlan || properties.length === 0) return;
    setBuildingPlan(true);
    try {
      // Recompute the exact set shown in this list (ids), so the action can't
      // drift from the displayed grouping.
      const plans = await buildPropertyPlans(properties.map((p) => p.id));
      setRecomputePlans(plans);
      setRecomputeOpen(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to compute changes");
    } finally {
      setBuildingPlan(false);
    }
  };

  const handleApplyRecompute = async () => {
    if (!recomputePlans) return;
    setApplyingPlan(true);
    try {
      const { updated, failed } = await applyPlans(recomputePlans);
      if (failed > 0) toast.error(`Updated ${updated} of ${updated + failed} (${failed} failed)`);
      else if (updated > 0) toast.success(`Updated ${updated} ${updated === 1 ? "property" : "properties"}`);
      else toast.info("No changes to apply");
      onApplied?.();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to apply changes");
    } finally {
      setApplyingPlan(false);
      setRecomputeOpen(false);
      setRecomputePlans(null);
    }
  };

  const filtered = properties.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      (p.address ?? "").toLowerCase().includes(q) ||
      (p.city ?? "").toLowerCase().includes(q) ||
      (p.state ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      {/* Info + bulk action */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{properties.length}</span> properties in{" "}
          <span className="font-medium text-foreground">{projectName}</span>
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={handleRecomputeAll}
          disabled={buildingPlan || properties.length === 0}
          className="gap-1.5 shrink-0"
        >
          {buildingPlan ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Checking…
            </>
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh status
            </>
          )}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          id="property-search"
          placeholder="Search by name, address, city..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* List */}
      <div className="grid gap-2">
        {filtered.map((property, i) => (
          <button
            key={property.id}
            onClick={() => onSelect(property)}
            className="group text-left animate-fade-in"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <Card className="transition-all duration-200 hover:border-primary/40 cursor-pointer">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Home className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{property.name}</p>
                  {(property.address || property.city) && (
                    <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {[property.address, property.city, property.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
                {property.property_type && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {property.property_type}
                  </Badge>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <Home className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {search ? "No properties match your search." : "No properties in this project."}
          </p>
        </div>
      )}

      <RecomputeReviewDialog
        open={recomputeOpen}
        plans={recomputePlans ?? []}
        applying={applyingPlan}
        onConfirm={handleApplyRecompute}
        onCancel={() => {
          if (applyingPlan) return;
          setRecomputeOpen(false);
          setRecomputePlans(null);
        }}
      />
    </div>
  );
}
